import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { withStyles, Box, BodyText, Card, Tooltip, TextField } from 'vopay';
import { Button, CircleButton } from 'UI';
import { Dialog } from '@/providers/Dialog.provider';
import styles from './IpAddressDialog.module.scss';

const cn = withStyles(styles);

// Ported from the backend's VIOMultiIPValidator (lib/util/validators/vio_multi_ip_validator.class.inc)
// so the frontend rejects the same things the API will — IPv4, IPv6 (incl. zone id), no ranges/CIDR.
const IP_ADDRESS_REGEX =
  /^\s*((([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5]))\s*$|^\s*((([0-9A-Fa-f]{1,4}:){7}([0-9A-Fa-f]{1,4}|:))|(([0-9A-Fa-f]{1,4}:){6}(:[0-9A-Fa-f]{1,4}|((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3})|:))|(([0-9A-Fa-f]{1,4}:){5}(((:[0-9A-Fa-f]{1,4}){1,2})|:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3})|:))|(([0-9A-Fa-f]{1,4}:){4}(((:[0-9A-Fa-f]{1,4}){1,3})|((:[0-9A-Fa-f]{1,4})?:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){3}(((:[0-9A-Fa-f]{1,4}){1,4})|((:[0-9A-Fa-f]{1,4}){0,2}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){2}(((:[0-9A-Fa-f]{1,4}){1,5})|((:[0-9A-Fa-f]{1,4}){0,3}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){1}(((:[0-9A-Fa-f]{1,4}){1,6})|((:[0-9A-Fa-f]{1,4}){0,4}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(:(((:[0-9A-Fa-f]{1,4}){1,7})|((:[0-9A-Fa-f]{1,4}){0,5}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:)))(%.+)?\s*$/;

function parseIpList(raw: string): string[] {
  return raw
    .split(',')
    .map((ip) => ip.trim())
    .filter((ip) => ip !== '');
}

type IpAddressDialogProps = {
  onConfirm: () => void;
  onCancel: () => void;
  onSubmit: (ips: string[]) => Promise<void>;
  existingIps: string[];
  // When set, the dialog opens in "Edit" mode for this single IP: pre-filled, single-value field,
  // "Save" copy, and excluded from the duplicate check.
  editingIp?: string;
};

// Opened via useDialog().open() with { bare: true } (see AuthorizedIpsPage.tsx), so this component owns
// its full card chrome — DialogProvider supplies only centering/stacking/overlay, no default card.
export default function IpAddressDialog({
  onConfirm,
  onCancel,
  onSubmit,
  existingIps,
  editingIp,
}: IpAddressDialogProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Settings.AuthorizedIpsSettings.Dialog' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const isEditMode = editingIp !== undefined;

  const [value, setValue] = useState(editingIp ?? '');
  const [touched, setTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const ips = isEditMode ? (value.trim() ? [value.trim()] : []) : parseIpList(value);
  const comparableExistingIps = isEditMode ? existingIps.filter((ip) => ip !== editingIp) : existingIps;
  const invalidIp = ips.find((ip) => !IP_ADDRESS_REGEX.test(ip)) ?? null;
  const duplicateIp = !invalidIp
    ? (ips.find((ip, index) => comparableExistingIps.includes(ip) || ips.indexOf(ip) !== index) ?? null)
    : null;

  const validationError = invalidIp
    ? t('invalidIp', { defaultValue: "{{value}} isn't a valid IP address.", value: invalidIp })
    : duplicateIp
      ? t('duplicateIp', { defaultValue: '{{value}} is already authorized.', value: duplicateIp })
      : null;

  const error = submitError ?? (touched ? validationError : null);
  const canSubmit = ips.length > 0 && !validationError && !isSubmitting;

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    setValue(event.currentTarget.value);
    setSubmitError(null);
  }

  function handleBlur() {
    setTouched(true);
  }

  async function handleSubmit() {
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      await onSubmit(ips);
      onConfirm();
    } catch (submitException) {
      setSubmitError(
        submitException instanceof Error
          ? submitException.message
          : t('submitError', { defaultValue: 'Something went wrong. Please try again.' }),
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Card className={cn('card')} hoverEffect={false}>
      <div className={cn('header')}>
        <Dialog.Title level="4" className={cn('title')}>
          {isEditMode
            ? t('editTitle', { defaultValue: 'Edit IP address' })
            : t('title', { defaultValue: 'Add IP address' })}
        </Dialog.Title>

        <CircleButton icon="close" size="large" circleFill aria-label={tGlobal('Buttons.close')} onClick={onCancel} />
      </div>

      <div className={cn('body')}>
        <Box flx="col" gapSize="8px">
          <Box flx="row" gapSize="2px" ai="center">
            <BodyText>{t('label', { defaultValue: 'IP address' })}</BodyText>
            <span className={cn('required')}>*</span>

            {!isEditMode && (
              <Tooltip
                icon="info"
                information={
                  <BodyText>
                    {t('tooltip', {
                      defaultValue: 'Once you add an address, only the addresses you list can call the API.',
                    })}
                  </BodyText>
                }
              />
            )}
          </Box>

          <TextField
            name="ipAddress"
            variant="outlineShadow"
            value={value}
            placeholder={isEditMode ? undefined : t('placeholder', { defaultValue: 'eg: 203.0.113.10, 203.0.113.11' })}
            error={error}
            onChange={handleChange}
            onBlur={handleBlur}
            autoFocus
          />

          <BodyText size="sm" className={cn('helperText')}>
            {isEditMode
              ? t('editHelperText', { defaultValue: "Enter an IPv4 or IPv6 address. Ranges aren't supported." })
              : t('helperText', {
                  defaultValue:
                    "Enter one or more IPv4 or IPv6 addresses, separated by commas. Ranges aren't supported.",
                })}
          </BodyText>
        </Box>
      </div>

      <div className={cn('footer')}>
        <Button colorScheme="primary" className={cn('submit')} disabled={!canSubmit} onClick={handleSubmit}>
          {isEditMode
            ? isSubmitting
              ? t('savingButton', { defaultValue: 'Saving...' })
              : t('saveButton', { defaultValue: 'Save' })
            : isSubmitting
              ? t('submittingButton', { defaultValue: 'Adding...' })
              : t('submitButton', { defaultValue: 'Add IP Address' })}
        </Button>
      </div>
    </Card>
  );
}
